import type { HeatmapCell } from '@/types/api';

// Minimal custom-SVG heatmap: 7 rows (days) × N columns (weeks). Intensity
// scales with cell count. No external dependency, keeps stats bundle lean.
//
// We key intensity off `completed_count` (the field the Go repo returns)
// rather than the total session count, so skipped/pending sessions don't
// light up the grid.
export function HeatmapGrid({ cells }: { cells: HeatmapCell[] }) {
  const byDate = new Map<string, number>();
  let max = 0;
  for (const c of cells) {
    const n = c.completed_count ?? c.session_count ?? 0;
    byDate.set(c.date.slice(0, 10), n);
    if (n > max) max = n;
  }

  // Build a 90-day window ending today.
  const today = new Date();
  const days: { date: string; count: number }[] = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    days.push({ date: iso, count: byDate.get(iso) ?? 0 });
  }

  const cell = 12;
  const gap = 3;
  const rows = 7;
  const cols = Math.ceil(days.length / rows);
  const width = cols * (cell + gap);
  const height = rows * (cell + gap);

  return (
    <svg width={width} height={height} className="max-w-full">
      {days.map((d, i) => {
        const col = Math.floor(i / rows);
        const row = i % rows;
        const intensity = max === 0 ? 0 : d.count / max;
        const alpha = d.count === 0 ? 0.12 : 0.25 + intensity * 0.75;
        return (
          <rect
            key={d.date}
            x={col * (cell + gap)}
            y={row * (cell + gap)}
            width={cell}
            height={cell}
            rx={2}
            fill="var(--color-accent)"
            fillOpacity={alpha}
          />
        );
      })}
    </svg>
  );
}
